/**
 * Links into the user guide, by the stable page ids it publishes.
 *
 * The guide resolves `/help/id/<id>` to wherever the page lives now, so a page
 * the guide retitles or moves keeps working here. The ids are the guide's
 * frontmatter `id` values (socratiq-www `src/content/docs/help/`); one missing
 * from that site renders its 404, so the list below is the contract.
 *
 * @module components/shell/guide-links
 */

/** The guide's home, which the sidebar footer links to. */
export const USER_GUIDE_URL = "https://socratiq.ie/help";

/**
 * Every guide page the app links to, with whether the guide shows it only to a
 * Client Admin. The guide answers any other reader with a 404, so the app
 * offers those pages only to someone who manages the tenant.
 */
const GUIDE_PAGES = {
  "at-a-glance": { clientAdminOnly: false },
  "creating-a-programme": { clientAdminOnly: false },
  "developing-a-programme": { clientAdminOnly: false },
  "building-an-assigned-module": { clientAdminOnly: false },
  "award-pathways": { clientAdminOnly: false },
  "programme-narrative": { clientAdminOnly: false },
  "checking-traceability": { clientAdminOnly: false },
  "readiness-and-outputs": { clientAdminOnly: false },
  "review-and-approval": { clientAdminOnly: false },
  "programme-teams": { clientAdminOnly: false },
  "programme-lifecycle": { clientAdminOnly: false },
  "programme-portfolio": { clientAdminOnly: false },
  "client-administration": { clientAdminOnly: true },
  "client-admin-programmes-and-teams": { clientAdminOnly: true },
  "client-admin-provider-settings": { clientAdminOnly: true },
  "client-admin-access-and-permissions": { clientAdminOnly: true },
  "client-admin-disciplines": { clientAdminOnly: true },
  "client-admin-governance-and-audit": { clientAdminOnly: true },
  "client-admin-reports": { clientAdminOnly: true },
} as const satisfies Record<string, { clientAdminOnly: boolean }>;

export type GuidePageId = keyof typeof GUIDE_PAGES;

/** What a route may put in its `handle` to offer its guide page. */
export interface RouteGuideHandle {
  /**
   * The guide page about this screen. A route without one offers its nearest
   * ancestor's, so a section names a page only where it differs.
   */
  guide?: GuidePageId;
}

/** The address of one guide page. */
export function guidePageUrl(id: GuidePageId): string {
  return `${USER_GUIDE_URL}/id/${encodeURIComponent(id)}`;
}

/** Whether the guide would show this page to the reader rather than a 404. */
export function guidePageVisible(id: GuidePageId, canManageTenant: boolean): boolean {
  return canManageTenant || !GUIDE_PAGES[id].clientAdminOnly;
}
